"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import "./AppointmentBookingCard.css";

import usePatientAvailability from "../../../../appointments/hooks/usePatientAvailability";
import useRendezvous from "../../../../appointments/hooks/useRendezvous";
import { getCurrentUser } from "../../../../authPatient/redux/thunks/authThunks";

/* ---------------- Helpers (mêmes principes que Scheduler) ---------------- */
const pad = (n) => String(n).padStart(2, "0");

const dateKey = (d) => {
  const y = d.getFullYear();
  const m = d.getMonth();
  const day = d.getDate();
  return new Date(Date.UTC(y, m, day)).toISOString().slice(0, 10);
};

const getErrorText = (err, fallback) => {
  if (!err) return fallback || "An error occurred";
  const data = err.data || err.response?.data || err;
  return (
    (typeof data?.message === "string" && data.message) ||
    (Array.isArray(data?.message) && data.message[0]) ||
    data?.error ||
    data?.detail ||
    err.message ||
    fallback ||
    "An error occurred"
  );
};

const WEEK_DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M8 2v3M16 2v3" stroke="#1e1e1e" strokeWidth="1.4" strokeLinecap="round" />
    <path
      d="M3 9h18M5 6h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z"
      stroke="#1e1e1e"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const startOfWeekMonday = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - day);
  return x;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

export default function AppointmentBookingCard({ doctor, onBooked = () => {} }) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [visitType, setVisitType] = useState("clinic");
  const [weekStart, setWeekStart] = useState(() => startOfWeekMonday(new Date()));
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  const [toast, setToast] = useState({ open: false, type: "success", text: "" });
  const [isBooking, setIsBooking] = useState(false);

  const lastLoadedKeyRef = useRef(null);
  const lastBookedRef = useRef(null);

  // hooks (mêmes que Scheduler)
  const { slotsByKey, loadingSlots, slotsError, loadSlots } = usePatientAvailability();
  const { bookRendezvous, creating: creatingRDV, createError: rdvError, lastCreated } = useRendezvous();

  // medecinId robuste (même principe)
  const rawMedecinId =
    doctor?.id ??
    doctor?.medecinId ??
    doctor?.userId ??
    doctor?.doctorId ??
    doctor?.doctor?.id ??
    doctor?.doctor?.userId;

  const medecinId = rawMedecinId != null ? String(rawMedecinId) : null;
  const hasValidMedecinId = Boolean(medecinId && medecinId.trim().length > 0);

  // ✅ VISITS traduit
  const VISITS = useMemo(
    () => [
      { key: "clinic", label: t("appointmentBookingCard.modes.inPerson"), mode: "in-person" },
      { key: "video", label: t("appointmentBookingCard.modes.videoCall"), mode: "video" }
    ],
    [t]
  );

  // get user
  useEffect(() => {
    dispatch(getCurrentUser());
  }, [dispatch]);

  // semaine (7 jours)
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }).map((_, i) => {
      const date = addDays(weekStart, i);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const isToday = date.toDateString() === today.toDateString();
      const disabled = date < today; // option: bloque dates passées
      return { date, dayNumber: date.getDate(), isToday, disabled };
    });
  }, [weekStart]);

  // charger slots quand date change (même logique)
  useEffect(() => {
    if (!selectedDate || !hasValidMedecinId || loadingSlots) return;

    const formatted = dateKey(selectedDate);
    const key = `${medecinId}|${formatted}`;

    if (key !== lastLoadedKeyRef.current) {
      lastLoadedKeyRef.current = key;
      loadSlots(medecinId, formatted);
    }
  }, [selectedDate, hasValidMedecinId, medecinId, loadSlots, loadingSlots]);

  // transformer slots → labels AM/PM (même logique)
  const availableTimes = useMemo(() => {
    if (!selectedDate || !hasValidMedecinId) return [];

    const key = `${medecinId}|${dateKey(selectedDate)}`;
    const slots = slotsByKey?.[key] ?? [];

    const now = new Date();
    const isSameDay =
      selectedDate.getFullYear() === now.getFullYear() &&
      selectedDate.getMonth() === now.getMonth() &&
      selectedDate.getDate() === now.getDate();

    const set = new Set();
    const labels = [];

    for (const s of slots) {
      if (!s?.isAvailable) continue;
      if (!s?.startTime) continue;

      const dt = new Date(s.startTime);
      if (isSameDay && dt <= now) continue;

      const h = dt.getHours();
      const m = dt.getMinutes();
      const period = h >= 12 ? "PM" : "AM";
      const h12 = h % 12 || 12;
      const label = `${h12}:${pad(m)} ${period}`;

      if (!set.has(label)) {
        set.add(label);
        labels.push(label);
      }
    }

    return labels;
  }, [selectedDate, hasValidMedecinId, medecinId, slotsByKey]);

  // ✅ mois localisé
  const monthLabel = useMemo(() => {
    const m = new Intl.DateTimeFormat(i18n.language || "en", { month: "long" }).format(weekStart);
    return `${m} ${weekStart.getFullYear()}`;
  }, [weekStart, i18n.language]);

  // succès booking
  useEffect(() => {
    if (!lastCreated || lastCreated === lastBookedRef.current) return;

    lastBookedRef.current = lastCreated;

    setToast({
      open: true,
      type: "success",
      text: t("appointmentBookingCard.toast.successRequested", {
        date: selectedDate ? dateKey(selectedDate) : "",
        time: selectedTime || ""
      })
    });
    setTimeout(() => setToast({ open: false, type: "success", text: "" }), 3500);

    setIsBooking(false);
    setSelectedTime(null);

    onBooked?.({
      doctor,
      medecinId,
      date: selectedDate,
      time: selectedTime,
      visitType,
      appointment: lastCreated
    });
  }, [lastCreated, doctor, medecinId, selectedDate, selectedTime, visitType, onBooked, t]);

  // erreur booking
  useEffect(() => {
    if (!rdvError || !isBooking) return;

    setToast({ open: true, type: "error", text: getErrorText(rdvError, t("appointmentBookingCard.errors.generic")) });
    setTimeout(() => setToast({ open: false, type: "success", text: "" }), 3500);
    setIsBooking(false);
  }, [rdvError, isBooking, t]);

  const handlePrevWeek = () => {
    setWeekStart((w) => addDays(w, -7));
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const handleNextWeek = () => {
    setWeekStart((w) => addDays(w, 7));
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const handlePickDay = (d) => {
    if (d.disabled) return;
    setSelectedDate(d.date);
    setSelectedTime(null);
  };

  const handleBook = async () => {
    if (!hasValidMedecinId) {
      setToast({ open: true, type: "error", text: t("appointmentBookingCard.errors.missingDoctorId") });
      setTimeout(() => setToast({ open: false, type: "success", text: "" }), 2500);
      return;
    }

    if (!selectedDate || !selectedTime || isBooking || creatingRDV) return;
    setIsBooking(true);

    const key = `${medecinId}|${dateKey(selectedDate)}`;
    const slots = slotsByKey?.[key] ?? [];

    // matching slot exact (même logique)
    const matchingSlot = slots.find((slot) => {
      if (!slot?.startTime) return false;
      const dt = new Date(slot.startTime);
      const h = dt.getHours();
      const m = dt.getMinutes();
      const period = h >= 12 ? "PM" : "AM";
      const h12 = h % 12 || 12;
      return `${h12}:${pad(m)} ${period}` === selectedTime;
    });

    if (!matchingSlot) {
      setToast({ open: true, type: "error", text: t("appointmentBookingCard.errors.slotNotAvailable") });
      setTimeout(() => setToast({ open: false, type: "success", text: "" }), 3000);
      setIsBooking(false);
      return;
    }

    if (new Date(matchingSlot.startTime) <= new Date()) {
      setToast({ open: true, type: "error", text: t("appointmentBookingCard.errors.slotPassed") });
      setTimeout(() => setToast({ open: false, type: "success", text: "" }), 3000);
      setIsBooking(false);
      return;
    }

    const mode = VISITS.find((v) => v.key === visitType)?.mode || "in-person";

    const rdvPayload = {
      medecinId,
      startTime: matchingSlot.startTime,
      endTime: matchingSlot.endTime,
      type: mode === "in-person" ? "standard" : "standard" // adapte si backend différencie
    };

    try {
      await bookRendezvous(rdvPayload);
      await loadSlots(medecinId, dateKey(selectedDate));
    } catch (e) {
      setToast({ open: true, type: "error", text: getErrorText(e, t("appointmentBookingCard.errors.generic")) });
      setTimeout(() => setToast({ open: false, type: "success", text: "" }), 3000);
      setIsBooking(false);
    }
  };

  const disabled =
    !selectedDate ||
    !selectedTime ||
    isBooking ||
    creatingRDV ||
    !hasValidMedecinId;

  return (
    <>
      <div className="abc-card">
        <div className="abc-title">{t("appointmentBookingCard.title")}</div>

        <div className="abc-tabs" role="tablist" aria-label={t("appointmentBookingCard.visitTypeAria")}>
          {VISITS.map((v) => (
            <button
              key={v.key}
              className={`abc-tab ${visitType === v.key ? "isActive" : ""} ${v.key !== "clinic" ? "isDisabled" : ""}`}
              onClick={() => {
                // comme ton scheduler: video disabled
                if (v.key !== "clinic") return;
                setVisitType(v.key);
              }}
              type="button"
              aria-disabled={v.key !== "clinic"}
              title={v.key !== "clinic" ? t("appointmentBookingCard.videoUnavailableTooltip") : undefined}
            >
              {v.label}
            </button>
          ))}
        </div>

        {(slotsError || rdvError) && (
          <div className="abc-inlineError">
            {getErrorText(slotsError, "") || getErrorText(rdvError, t("appointmentBookingCard.errors.generic"))}
          </div>
        )}

        <div className="ab-month">
          <div className="abc-monthRow">
            <div className="abc-month">{monthLabel}</div>
            <div className="abc-nav">
              <button
                className="abc-navBtn"
                type="button"
                onClick={handlePrevWeek}
                aria-label={t("appointmentBookingCard.calendar.prevWeek")}
                title={t("appointmentBookingCard.calendar.prevWeek")}
              >
                ‹
              </button>
              <button
                className="abc-navBtn"
                type="button"
                onClick={handleNextWeek}
                aria-label={t("appointmentBookingCard.calendar.nextWeek")}
                title={t("appointmentBookingCard.calendar.nextWeek")}
              >
                ›
              </button>
            </div>
          </div>

          <div className="abc-week">
            {WEEK_DAYS.map((d) => (
              <div key={d} className="abc-weekDay">
                {t(`appointmentBookingCard.week.${d}`)}
              </div>
            ))}
          </div>

          <div className="abc-days">
            {weekDays.map((d) => {
              const active = selectedDate && d.date.toDateString() === selectedDate.toDateString();
              return (
                <button
                  key={d.date.toISOString()}
                  className={`abc-day ${active ? "isActive" : ""} ${d.isToday ? "isToday" : ""}`}
                  onClick={() => handlePickDay(d)}
                  type="button"
                  disabled={d.disabled}
                  title={d.disabled ? t("appointmentBookingCard.calendar.pastDate") : ""}
                  aria-label={t("appointmentBookingCard.calendar.dayAria", { day: String(d.dayNumber).padStart(2, "0") })}
                >
                  {String(d.dayNumber).padStart(2, "0")}
                </button>
              );
            })}
          </div>
        </div>

        <div className="abtimes">
          <div className="abc-subTitle">{t("appointmentBookingCard.times.title")}</div>

          <div className="abc-times">
            {!selectedDate && <div className="abc-empty">{t("appointmentBookingCard.times.selectDateFirst")}</div>}

            {selectedDate && loadingSlots && <div className="abc-empty">{t("appointmentBookingCard.times.loading")}</div>}

            {selectedDate && !loadingSlots && availableTimes.length === 0 && (
              <div className="abc-empty">{t("appointmentBookingCard.times.noTimes")}</div>
            )}

            {selectedDate &&
              !loadingSlots &&
              availableTimes.length > 0 &&
              availableTimes.slice(0, 6).map((time) => {
                const active = time === selectedTime;
                return (
                  <button
                    key={time}
                    className={`abc-time ${active ? "isActive" : ""}`}
                    onClick={() => setSelectedTime(time)}
                    type="button"
                    aria-label={t("appointmentBookingCard.times.slotAria", { time })}
                  >
                    {time}
                  </button>
                );
              })}
          </div>
        </div>

        <button className="abc-bookBtn" onClick={handleBook} type="button" disabled={disabled}>
          <span className="abc-bookIcon">
            <CalendarIcon />
          </span>
          {isBooking || creatingRDV ? t("appointmentBookingCard.actions.booking") : t("appointmentBookingCard.actions.book")}
        </button>

        {!hasValidMedecinId && (
          <div className="abc-inlineError">{t("appointmentBookingCard.errors.missingDoctorId")}</div>
        )}
      </div>

      {/* Toast */}
      <div className={`abc-toast ${toast.open ? "show" : ""} ${toast.type === "error" ? "isError" : ""}`}>
        {toast.text}
      </div>
    </>
  );
}
